import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

const HEX = /^(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * <paint-colour-picker> - colour + size selection for paint products on the
 * NestBuddy product page (see snippets/paint-colour-picker.liquid).
 *
 * Paint variants are Size x Shade Group, but customers only ever pick a
 * colour; its group (Light / Medium / Dark) is looked up here and decides
 * which variants the Size pills map to. The chosen variant is written into the
 * section's existing product form (hidden id + line item properties) and the
 * form's <nb-cart-stepper>, so adding to cart and the cart drawer / cart bar
 * behave exactly as they do for every other product.
 *
 * The shade list (~2,000 rows) is built once, the first time the dropdown
 * opens, and searching only toggles `hidden` on the existing rows.
 */
class PaintColourPicker extends HTMLElement {
  /** @type {Array<[string, string, string, string]>} */
  #shades = [];
  /** @type {Array<{ id: number, size: string, group: string, price: number, compare: number, available: boolean, min: number, max: number | null }>} */
  #variants = [];
  /** @type {Array<{ key: string, variant: number, shade: string, quantity: number }>} */
  #lines = [];
  /** @type {HTMLLIElement[]} */
  #items = [];
  /** @type {string[]} */
  #haystack = [];
  #activeIndex = -1;
  #selectedIndex = -1;
  /** @type {string | null} */
  #size = null;
  /** @type {(typeof this.#variants)[number] | null} */
  #variant = null;

  connectedCallback() {
    this.#shades = this.#readJson('[data-paint-shades]').filter(
      (row) => Array.isArray(row) && row.length >= 4 && row[0] && row[3]
    );
    this.#variants = this.#readJson('[data-paint-variants]');
    this.#lines = this.#readJson('[data-paint-cart]');

    this.trigger = this.querySelector('[data-paint-trigger]');
    this.triggerSwatch = this.querySelector('[data-paint-trigger-swatch]');
    this.triggerText = this.querySelector('[data-paint-trigger-text]');
    this.panel = this.querySelector('[data-paint-panel]');
    this.searchInput = this.querySelector('[data-paint-search]');
    this.list = this.querySelector('[data-paint-list]');
    this.empty = this.querySelector('[data-paint-empty]');
    this.sizes = this.querySelector('[data-paint-sizes]');
    this.sizeButtons = [...this.querySelectorAll('[data-paint-size]')];

    // Everything else lives in the section's buy panel around this element.
    const scope = this.closest('.nb-pdp__panel') ?? document;
    this.priceEl = scope.querySelector('.nb-pdp__price');
    this.compareEl = scope.querySelector('.nb-pdp__compare');
    this.saveEl = scope.querySelector('.nb-pdp__save');
    this.stockEl = scope.querySelector('.nb-pdp__stock');
    this.stockText = scope.querySelector('[data-paint-stock-text]');
    this.stepper = scope.querySelector('nb-cart-stepper');
    this.idInput = scope.querySelector('[data-role="add-form"] input[name="id"]');
    this.addButton = scope.querySelector('[data-role="add"]');
    this.propertyInputs = {
      colour: scope.querySelector('[data-paint-property="colour"]'),
      code: scope.querySelector('[data-paint-property="code"]'),
      group: scope.querySelector('[data-paint-property="group"]'),
    };

    this.trigger?.addEventListener('click', this.#onTriggerClick);
    this.trigger?.addEventListener('keydown', this.#onTriggerKeydown);
    this.searchInput?.addEventListener('input', this.#onSearch);
    this.searchInput?.addEventListener('keydown', this.#onSearchKeydown);
    this.list?.addEventListener('click', this.#onListClick);
    this.list?.addEventListener('mousedown', this.#keepSearchFocus);
    for (const button of this.sizeButtons) button.addEventListener('click', this.#onSizeClick);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);

    if (!this.#shades.length) {
      console.warn('[paint-colour-picker] No valid shades found for this product.');
      return;
    }

    const params = new URL(window.location.href).searchParams;
    const shadeParam = params.get('shade');
    if (shadeParam) {
      // A shared ?shade=&variant= link restores the size as well.
      const linked = this.#variants.find((v) => String(v.id) === params.get('variant'));
      if (linked) this.#size = linked.size;

      const wanted = shadeParam.trim().toLowerCase();
      const index = this.#shades.findIndex(([code]) => String(code).toLowerCase() === wanted);
      if (index >= 0) this.#selectShade(index);
    }
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener('pointerdown', this.#onOutsidePointer);
  }

  /**
   * @param {string} selector
   */
  #readJson(selector) {
    try {
      const parsed = JSON.parse(this.querySelector(selector)?.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn(`[paint-colour-picker] Invalid JSON in ${selector}:`, error);
      return [];
    }
  }

  /**
   * @param {number} cents
   */
  #money(cents) {
    return formatMoney(cents, Theme.moneyFormat, this.dataset.currency);
  }

  /**
   * @param {string} hex
   */
  #colour(hex) {
    const clean = String(hex || '').replace(/^#/, '');
    return HEX.test(clean) ? `#${clean}` : 'transparent';
  }

  /* ---- Dropdown ---- */

  get #isOpen() {
    return this.panel ? !this.panel.hidden : false;
  }

  #buildList() {
    if (this.#items.length || !this.list) return;

    const fragment = document.createDocumentFragment();
    const idPrefix = this.list.id;

    this.#shades.forEach(([code, name, hex], index) => {
      const item = document.createElement('li');
      item.id = `${idPrefix}-${index}`;
      item.className = 'nb-paint__option';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', String(index === this.#selectedIndex));
      item.dataset.index = String(index);

      const swatch = document.createElement('span');
      swatch.className = 'nb-paint__swatch';
      swatch.style.backgroundColor = this.#colour(hex);

      const label = document.createElement('span');
      label.className = 'nb-paint__option-label';
      label.textContent = `${code} - ${name}`;

      item.append(swatch, label);
      fragment.append(item);
      this.#items.push(item);
      this.#haystack.push(`${code} - ${name}`.toLowerCase());
    });

    this.list.append(fragment);
  }

  #open() {
    if (this.#isOpen || !this.panel || !this.#shades.length) return;

    this.#buildList();
    this.panel.hidden = false;
    this.trigger?.setAttribute('aria-expanded', 'true');

    if (this.searchInput) this.searchInput.value = '';
    this.#filter();
    this.#setActive(this.#selectedIndex >= 0 ? this.#selectedIndex : this.#firstVisible(), 'center');

    this.searchInput?.focus({ preventScroll: true });
    document.addEventListener('pointerdown', this.#onOutsidePointer);
  }

  /**
   * @param {boolean} [restoreFocus]
   */
  #close(restoreFocus = false) {
    if (!this.#isOpen || !this.panel) return;

    this.panel.hidden = true;
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.searchInput?.removeAttribute('aria-activedescendant');
    document.removeEventListener('pointerdown', this.#onOutsidePointer);

    if (restoreFocus) this.trigger?.focus();
  }

  #filter() {
    const query = (this.searchInput?.value || '').trim().toLowerCase();
    let visible = 0;

    for (let i = 0; i < this.#items.length; i++) {
      const item = this.#items[i];
      const show = !query || this.#haystack[i].includes(query);
      if (item.hidden === show) item.hidden = !show;
      if (show) visible++;
    }

    if (this.empty) this.empty.hidden = visible > 0;
  }

  #firstVisible() {
    return this.#items.findIndex((item) => !item.hidden);
  }

  /**
   * @param {number} from
   * @param {1 | -1} step
   */
  #nextVisible(from, step) {
    for (let i = from + step; i >= 0 && i < this.#items.length; i += step) {
      if (!this.#items[i].hidden) return i;
    }
    return from;
  }

  /**
   * @param {number} index
   * @param {'nearest' | 'center'} [block]
   */
  #setActive(index, block = 'nearest') {
    this.#items[this.#activeIndex]?.classList.remove('is-active');
    this.#activeIndex = index;

    const item = this.#items[index];
    if (!item || item.hidden) {
      this.searchInput?.removeAttribute('aria-activedescendant');
      return;
    }

    item.classList.add('is-active');
    this.searchInput?.setAttribute('aria-activedescendant', item.id);

    // Scroll the list only (not the page, as scrollIntoView would on mobile).
    const list = this.list;
    if (!list) return;
    const top = item.offsetTop;
    const bottom = top + item.offsetHeight;
    if (block === 'center') {
      list.scrollTop = top - (list.clientHeight - item.offsetHeight) / 2;
    } else if (top < list.scrollTop) {
      list.scrollTop = top;
    } else if (bottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = bottom - list.clientHeight;
    }
  }

  #onTriggerClick = () => {
    if (this.#isOpen) this.#close(true);
    else this.#open();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onTriggerKeydown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.#open();
    }
  };

  #onSearch = () => {
    this.#filter();
    if (this.list) this.list.scrollTop = 0;
    this.#setActive(this.#firstVisible());
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onSearchKeydown = (event) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#setActive(this.#activeIndex < 0 ? this.#firstVisible() : this.#nextVisible(this.#activeIndex, 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#setActive(this.#nextVisible(this.#activeIndex, -1));
        break;
      case 'Enter':
        event.preventDefault();
        if (this.#items[this.#activeIndex] && !this.#items[this.#activeIndex].hidden) {
          this.#selectShade(this.#activeIndex);
          this.#close(true);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.#close(true);
        break;
      case 'Tab':
        this.#close();
        break;
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #onListClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const item = event.target.closest('[role="option"]');
    if (!(item instanceof HTMLElement)) return;

    this.#selectShade(Number(item.dataset.index));
    this.#close(true);
  };

  /**
   * Keeps focus (and the mobile keyboard) on the search box while a row is
   * being clicked, so the list doesn't jump as the keyboard closes.
   *
   * @param {MouseEvent} event
   */
  #keepSearchFocus = (event) => {
    if (event.target instanceof Element && event.target.closest('[role="option"]')) event.preventDefault();
  };

  /**
   * @param {PointerEvent} event
   */
  #onOutsidePointer = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) this.#close();
  };

  /* ---- Selection ---- */

  /**
   * @param {number} index
   */
  #selectShade(index) {
    const row = this.#shades[index];
    if (!row) return;

    const [code, name, hex, group] = row;
    const label = `${code} - ${name}`;

    this.#items[this.#selectedIndex]?.setAttribute('aria-selected', 'false');
    this.#items[index]?.setAttribute('aria-selected', 'true');
    this.#selectedIndex = index;

    if (this.triggerText) this.triggerText.textContent = label;
    if (this.triggerSwatch) {
      this.triggerSwatch.style.backgroundColor = this.#colour(hex);
      this.triggerSwatch.hidden = false;
    }
    this.trigger?.classList.add('nb-paint__trigger--selected');

    const { colour, code: codeInput, group: groupInput } = this.propertyInputs;
    if (colour) colour.value = label;
    if (codeInput) codeInput.value = String(code);
    if (groupInput) groupInput.value = String(group);

    this.#applyGroup(String(group));
  }

  /**
   * Maps every Size pill to its variant within the chosen Shade Group and
   * picks a size: the current one if this group has it, otherwise the first
   * available.
   *
   * @param {string} group
   */
  #applyGroup(group) {
    const hidePrices = this.hasAttribute('data-hide-size-prices');
    let keep = null;
    let firstAvailable = null;
    let firstAny = null;

    for (const button of this.sizeButtons) {
      const size = button.dataset.paintSize;
      const variant = this.#variants.find((v) => v.size === size && v.group === group) ?? null;

      button.hidden = !variant;
      button.dataset.variantId = variant ? String(variant.id) : '';
      if (!variant) continue;

      const priceEl = button.querySelector('[data-paint-size-price]');
      if (priceEl) {
        priceEl.textContent = this.#money(variant.price);
        priceEl.hidden = hidePrices;
      }
      button.classList.toggle('nb-paint__size--unavailable', !variant.available);

      firstAny ??= variant;
      if (variant.available) firstAvailable ??= variant;
      if (size === this.#size) keep = variant;
    }

    if (this.sizes) this.sizes.hidden = false;
    this.#selectVariant(keep ?? firstAvailable ?? firstAny);
  }

  /**
   * @param {MouseEvent} event
   */
  #onSizeClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.currentTarget);
    const variant = this.#variants.find((v) => String(v.id) === button.dataset.variantId);
    if (variant) this.#selectVariant(variant);
  };

  /**
   * @param {(typeof this.#variants)[number] | null} variant
   */
  #selectVariant(variant) {
    this.#variant = variant;
    if (variant) this.#size = variant.size;

    for (const button of this.sizeButtons) {
      const current = !!variant && button.dataset.variantId === String(variant.id);
      button.setAttribute('aria-pressed', String(current));
      if (current) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    }

    this.#renderPrice(variant);
    this.#renderStock(variant);

    if (this.idInput && variant) this.idInput.value = String(variant.id);
    if (this.addButton) {
      this.addButton.disabled = !variant?.available;
      this.addButton.textContent = variant?.available
        ? this.addButton.dataset.paintAddText || 'Add to cart'
        : variant
          ? this.addButton.dataset.paintSoldOutText || 'Sold out'
          : this.addButton.dataset.paintUnavailableText || 'Unavailable';
    }

    this.#syncStepper();
    this.#updateUrl();
  }

  /**
   * @param {(typeof this.#variants)[number] | null} variant
   */
  #renderPrice(variant) {
    if (!variant) return;

    if (this.priceEl) this.priceEl.textContent = this.#money(variant.price);

    const onSale = variant.compare > variant.price;
    if (this.compareEl) {
      this.compareEl.hidden = !onSale;
      this.compareEl.textContent = onSale ? this.#money(variant.compare) : '';
    }
    if (this.saveEl) {
      this.saveEl.hidden = !onSale;
      this.saveEl.textContent = onSale
        ? `Save ${Math.floor(((variant.compare - variant.price) * 100) / variant.compare)}%`
        : '';
    }
  }

  /**
   * @param {(typeof this.#variants)[number] | null} variant
   */
  #renderStock(variant) {
    if (!this.stockEl) return;
    this.stockEl.hidden = !variant;
    this.stockEl.classList.toggle('nb-pdp__stock--out', !variant?.available);
    if (this.stockText) this.stockText.textContent = variant?.available ? 'In stock' : 'Out of stock';
  }

  /**
   * Points the form's add/stepper control at the chosen variant + colour.
   * Lines are matched by variant AND shade code, since the same variant can
   * be in the cart once per colour.
   */
  #syncStepper() {
    const stepper = /** @type {any} */ (this.stepper);
    const variant = this.#variant;
    const code = this.propertyInputs.code?.value;
    if (!stepper || !variant || !code) return;

    const line = this.#lines.find((l) => l.variant === variant.id && l.shade === code);
    const config = {
      variantId: variant.id,
      quantity: line?.quantity ?? 0,
      lineKey: line?.key ?? null,
      min: variant.min || 1,
      max: variant.max ?? null,
    };

    customElements.whenDefined('nb-cart-stepper').then(() => {
      if (!stepper.busy) stepper.configure(config);
    });
  }

  #updateUrl() {
    const code = this.propertyInputs.code?.value;
    if (!code) return;

    const url = new URL(window.location.href);
    url.searchParams.set('shade', code);
    if (this.#variant) url.searchParams.set('variant', String(this.#variant.id));
    window.history.replaceState(window.history.state, '', url.toString());
  }

  /* ---- Cart sync ---- */

  /**
   * Re-reads this product's cart lines after any cart change (the stepper,
   * the cart drawer, ...) so switching back to a colour already in the cart
   * shows its quantity stepper rather than a fresh Add button.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise?.then(() => this.#refreshLines()).catch(() => {});
  };

  async #refreshLines() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      const productId = Number(this.dataset.productId);

      this.#lines = cart.items
        .filter((/** @type {any} */ item) => item.product_id === productId && item.properties?._shade_code)
        .map((/** @type {any} */ item) => ({
          key: item.key,
          variant: item.variant_id,
          shade: item.properties._shade_code,
          quantity: item.quantity,
        }));

      this.#syncStepper();
    } catch (error) {
      console.warn('[paint-colour-picker] Failed to refresh cart lines:', error);
    }
  }
}

if (!customElements.get('paint-colour-picker')) {
  customElements.define('paint-colour-picker', PaintColourPicker);
}
